import React, { useState, useCallback, Fragment, Children, cloneElement, useMemo } from 'react';
import { Form, FormSpy } from 'react-final-form';
import PropTypes from 'prop-types';

const Wizard = ({
  onSubmit,
  validate,
  validateOnBlur,
  children,
  renderFooter,
  renderHeader,
  afterNavigate,
  subscription,
  headerSubscription,
  footerSubscription,
}) => {
  const [step, setStep] = useState(0);
  const childList = useMemo(() => Children.toArray(children), [children]);
  const stepCount = useMemo(() => childList.length, [childList]);

  const onNext = useCallback(() => {
    if (step < stepCount) {
      setStep(step + 1);
    }

    afterNavigate && afterNavigate();
  }, [afterNavigate, step, stepCount]);

  const onPrev = useCallback(() => {
    if (step > 0) {
      setStep(step - 1);
    }

    afterNavigate && afterNavigate();
  }, [afterNavigate, step]);

  /**
   * NOTE: Both validate and handleSubmit switching are implemented
   * here because 🏁 Redux Final Form does not accept changes to those
   * functions once the form has been defined.
   */

  const onValidate = useCallback(
    async (values, form) => {
      const activePage = childList[step];

      return await (activePage.props.validate
        ? activePage.props.validate(values, form)
        : validate(values, form));
    },
    [childList, step, validate],
  );

  const handleSubmit = useCallback(
    async (values, form) => {
      const isLastPage = step === stepCount - 1;
      const activePage = childList[step];

      return new Promise(async resolve => {
        if (isLastPage) {
          return resolve(await onSubmit(values));
        }

        if (activePage.props.validate) {
          const errors = await activePage.props.validate(values, form);

          if (Object.keys(errors).length > 0) {
            return resolve(errors);
          }
        }

        onNext();

        return resolve({});
      });
    },
    [step, stepCount, childList, onNext, onSubmit],
  );

  return (
    <Form
      onSubmit={handleSubmit}
      validate={onValidate}
      validateOnBlur={validateOnBlur}
      subscription={subscription}
      render={props => {
        const childrenProps = {
          onNext: props.handleSubmit,
          onPrev,
          step,
          hasPrev: step > 0,
          formProps: props,
          isLast: step === stepCount - 1,
        };

        return (
          <Fragment>
            {renderHeader && (
              <FormSpy
                step={step}
                subscription={headerSubscription}
                render={formProps => renderHeader({ ...childrenProps, formProps })}
              />
            )}
            {Children.map(children, (child, idx) =>
              cloneElement(
                child,
                {
                  ...childrenProps,
                  active: idx === step,
                  key: step + idx,
                },
                child.props.children,
              ),
            )}
            {renderFooter && (
              <FormSpy
                step={step}
                subscription={footerSubscription}
                render={formProps => renderFooter({ ...childrenProps, formProps })}
              />
            )}
          </Fragment>
        );
      }}
    />
  );
};

const subscriptionShape = {
  values: PropTypes.bool,
  validating: PropTypes.bool,
  submitting: PropTypes.bool,
  errors: PropTypes.bool,
  pristine: PropTypes.bool,
  validationErrors: PropTypes.bool,
  modified: PropTypes.bool,
};

const defaultSubscription = {
  pristine: true,
};

const partialSubscriptionShape = subscriptionShape;

const defaultPartialSubscription = undefined;

Wizard.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.object]),
  onSubmit: PropTypes.func.isRequired,
  renderHeader: PropTypes.func,
  renderFooter: PropTypes.func,
  afterNavigate: PropTypes.func,
  validate: PropTypes.func,
  validateOnBlur: PropTypes.bool,
  subscription: PropTypes.shape(subscriptionShape),
  headerSubscription: PropTypes.shape(partialSubscriptionShape),
  footerSubscription: PropTypes.shape(partialSubscriptionShape),
};

Wizard.defaultProps = {
  validateOnBlur: true,
  validate: () => ({}),
  afterNavigate: () => null,
  renderFooter: () => null,
  renderHeader: () => null,
  subscription: defaultSubscription,
  headerSubscription: defaultPartialSubscription,
  footerSubscription: defaultPartialSubscription,
};

export default Wizard;
